import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { isDataLoaded } from '../../types/data-providers';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { OverviewCard } from '../OverviewCard';
import { LocationCardContentLoader } from './LocationCardContentLoader';
import * as MuiIcons from '@mui/icons-material';
import { AppRoute } from '../../routes';
import { useDeviceCount } from '../../data/DataProviders/DeviceCountProvider';
import { useSiteOnlineStatusData } from '../../data/DataProviders/SiteOnlineStatusDataProvider';

type Props = {
    id: string;
    name: string;
    alarmCount: number;
    siteCount: number;
};

export const LocationCard: React.FC<Props> = (props: Props) => {
    const { t } = useLanguageLocale();
    const deviceCount = useDeviceCount();
    const SiteOnlineStatusDataProvider = useSiteOnlineStatusData();
    const [hasRecentAlarm, setRecentAlarm] = useState(false);
    const [alarmCount, setAlarmCount] = useState(0);

    const offlineCountdata = isDataLoaded(SiteOnlineStatusDataProvider)
        ? SiteOnlineStatusDataProvider.value.offlinedevicecount
        : 0;
    const status = isDataLoaded(SiteOnlineStatusDataProvider) ? SiteOnlineStatusDataProvider.value.status : undefined;

    useEffect(() => {
        setRecentAlarm(props.alarmCount > 0);
        setAlarmCount(props.alarmCount);
    }, [props.alarmCount]); // eslint-disable-line react-hooks/exhaustive-deps

    const subtitle =
        alarmCount === undefined
            ? t('DASHBOARD.LOCATION_CARD.HEADER.SUBTITLE.NO_DATA')
            : alarmCount > 0
            ? t('DASHBOARD.LOCATION_CARD.HEADER.SUBTITLE.ALARM', {
                  count: alarmCount,
              })
            : t('DASHBOARD.LOCATION_CARD.HEADER.SUBTITLE.NORMAL');

    const headerInfo = isDataLoaded(deviceCount)
        ? [
              t('DASHBOARD.LOCATION_CARD.HEADER.INFO.SITE', { count: props.siteCount }),
              t('DASHBOARD.LOCATION_CARD.HEADER.INFO.SEPARATOR'),
              t('DASHBOARD.LOCATION_CARD.HEADER.INFO.DEVICE', { count: deviceCount.value ? deviceCount.value : 0 }),
          ].join('')
        : '';

    const navigationLabel = t('DASHBOARD.LOCATION_CARD.FOOTER.VIEW_LOCATION');
    const navigationTargetAppRoute: AppRoute = {
        type: 'OverviewLocation',
        locationId: props.id,
    };

    const data = (isDataLoaded(SiteOnlineStatusDataProvider)
      ? SiteOnlineStatusDataProvider.value.status === false
      : !status)
        ? {
              label: t('DASHBOARD.LOCATION_CARD.BODY.OFFLINE_DEVICE', {
                  count: isDataLoaded(SiteOnlineStatusDataProvider)
                      ? SiteOnlineStatusDataProvider.value.offlinedevicecount
                      : 0,
              }),
              bold: (offlineCountdata || 0) > 0,
              icon: MuiIcons.CloudOff,
              className: clsx({ errorColor: offlineCountdata }),
          }
        : {
              label: t('DASHBOARD.LOCATION_CARD.BODY.ONLINE'),
              bold: !status,
              icon: MuiIcons.CloudDone,
              className: clsx({ primaryColor: !status }),
          };

    const statusItems = [
        {
            label: t('DASHBOARD.LOCATION_CARD.BODY.ALARMS', { count: alarmCount }),
            bold: (alarmCount || 0) > 0,
            icon: MuiIcons.NotificationsActive,
            className: clsx({ errorColor: hasRecentAlarm }),
        },
        // TODO: comment as per MVP scope , may be needed post MVP
        //   {
        //       label: tNoData('DASHBOARD.LOCATION_CARD.BODY.EVENTS', locationData.value.alarmCount),
        //       bold: (locationData.value.eventCount || 0) > 0,
        //       icon: MuiIcons.Info,
        //       className: clsx({ primaryColor: hasEvents }),
        //   },
        {
            label: data.label,
            bold: data.bold,
            icon: data.icon,
            className: data.className,
        },
    ];

    return (
        <div data-testid="location-card">
            <OverviewCard
                id={props.id}
                title={props.name}
                subtitle={subtitle}
                headerInfo={headerInfo}
                hasAlarm={hasRecentAlarm}
                dataState={deviceCount.state}
                loadingComponent={LocationCardContentLoader}
                statusItems={statusItems}
                navigationLabel={navigationLabel}
                navigationTargetAppRoute={navigationTargetAppRoute}
                statusDataState={SiteOnlineStatusDataProvider.state}
            />
        </div>
    );
};
